const roundFabricNumber = (value) => Number(value.toFixed(4));

const scaleNumber = (value, scale) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return value;
  }

  return roundFabricNumber(value * scale);
};

const scaleFabricTransform = (object, scaleX, scaleY) => {
  if (!object || typeof object !== 'object') {
    return;
  }

  if (typeof object.left === 'number') {
    object.left = scaleNumber(object.left, scaleX);
  }
  if (typeof object.top === 'number') {
    object.top = scaleNumber(object.top, scaleY);
  }
  if (typeof object.scaleX === 'number') {
    object.scaleX = scaleNumber(object.scaleX, scaleX);
  } else if (typeof object.scaleX === 'undefined') {
    object.scaleX = scaleNumber(1, scaleX);
  }
  if (typeof object.scaleY === 'number') {
    object.scaleY = scaleNumber(object.scaleY, scaleY);
  } else if (typeof object.scaleY === 'undefined') {
    object.scaleY = scaleNumber(1, scaleY);
  }
};

const scaleLinkedCanvasTransforms = (object, scaleX, scaleY) => {
  if (object?.type !== 'MagicFrame') {
    return;
  }

  // MagicFrame serializes the editable person as a separate canvas-space
  // transform. Scaling only the outer frame would leave the person at the
  // previous DPI, making it appear displaced and at the wrong size.
  scaleFabricTransform(object?.editingImgProps, scaleX, scaleY);
  scaleFabricTransform(object?.lastProps, scaleX, scaleY);
};

const scaleAbsoluteClipPath = (object, scaleX, scaleY) => {
  const clipPath = object?.clipPath;

  if (!clipPath?.absolutePositioned) {
    return;
  }

  scaleFabricTransform(clipPath, scaleX, scaleY);
};

const scaleTopLevelFabricObject = (object, scaleX, scaleY) => {
  scaleFabricTransform(object, scaleX, scaleY);
  scaleLinkedCanvasTransforms(object, scaleX, scaleY);
  scaleAbsoluteClipPath(object, scaleX, scaleY);
};

const getCanvasRoot = (canvasJson) => {
  if (canvasJson?.canvas && typeof canvasJson.canvas === 'object') {
    return canvasJson.canvas;
  }

  return canvasJson;
};

const getRenderedObjectSize = (object) => {
  if (!object || typeof object !== 'object') {
    return null;
  }

  const width = Number(object.width);
  const height = Number(object.height);
  const scaleX = Number(object.scaleX ?? 1);
  const scaleY = Number(object.scaleY ?? 1);

  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    !Number.isFinite(scaleX) ||
    !Number.isFinite(scaleY)
  ) {
    return null;
  }

  return {
    width: roundFabricNumber(width * scaleX),
    height: roundFabricNumber(height * scaleY),
  };
};

export const getCanvasJsonBackgroundRenderedSize = (canvasJson) => {
  const parsed =
    typeof canvasJson === 'string' ? JSON.parse(canvasJson) : canvasJson;
  const root = getCanvasRoot(parsed);

  return getRenderedObjectSize(root?.backgroundImage);
};

export const scaleCanvasJsonForDpiChange = (
  canvasJson,
  sourceDimensions,
  targetDimensions
) => {
  const sourceWidth = Number(sourceDimensions?.width);
  const sourceHeight = Number(sourceDimensions?.height);
  const targetWidth = Number(targetDimensions?.width);
  const targetHeight = Number(targetDimensions?.height);

  if (
    !Number.isFinite(sourceWidth) ||
    !Number.isFinite(sourceHeight) ||
    !Number.isFinite(targetWidth) ||
    !Number.isFinite(targetHeight) ||
    sourceWidth <= 0 ||
    sourceHeight <= 0 ||
    targetWidth <= 0 ||
    targetHeight <= 0
  ) {
    throw new Error('Invalid canvas dimensions for DPI migration');
  }

  const parsed =
    typeof canvasJson === 'string' ? JSON.parse(canvasJson) : canvasJson;
  const migrated = JSON.parse(JSON.stringify(parsed));
  const root = getCanvasRoot(migrated);
  const scaleX = targetWidth / sourceWidth;
  const scaleY = targetHeight / sourceHeight;

  if (Array.isArray(root.objects)) {
    root.objects.forEach((object) =>
      scaleTopLevelFabricObject(object, scaleX, scaleY)
    );
  }

  // A canvas clipPath lives in canvas coordinates, unlike a relative object
  // clipPath, so it must follow the DPI transform too.
  scaleTopLevelFabricObject(root.clipPath, scaleX, scaleY);
  scaleTopLevelFabricObject(root.backgroundImage, scaleX, scaleY);
  scaleTopLevelFabricObject(root.overlayImage, scaleX, scaleY);

  // Fabric does not normally serialize canvas dimensions, but some legacy
  // payloads do. Keep those values aligned so loadFromJSON cannot restore the
  // old DPI dimensions after the editor has created the correctly sized canvas.
  if (typeof root.width === 'number' && Number.isFinite(root.width)) {
    root.width = roundFabricNumber(targetWidth);
  }
  if (typeof root.height === 'number' && Number.isFinite(root.height)) {
    root.height = roundFabricNumber(targetHeight);
  }

  return JSON.stringify(migrated);
};
